// src/utils/transactionDiagnostics.js

/**
 * Utility to log detailed transaction information for debugging
 * @param {object} txParams - Transaction parameters 
 * @param {string} stage - Transaction stage (e.g., 'preparation', 'execution')
 */
export const logTransactionDetails = (txParams, stage = 'preparation') => {
  console.group(`Transaction Details (${stage})`);
  console.log('Transaction To:', txParams.to);
  if (txParams.from) console.log('Transaction From:', txParams.from);
  console.log('Value:', typeof txParams.value === 'bigint' 
    ? `${txParams.value.toString()} wei` 
    : txParams.value);
  
  if (txParams.data) {
    console.log('Data Length:', txParams.data.length);
    console.log('Data Prefix:', txParams.data.substring(0, 10));
    
    // Try to identify the function being called
    const functionSelector = txParams.data.substring(0, 10);
    const knownSelectors = {
      '0x38ed1739': 'swapExactTokensForTokens',
      '0x7ff36ab5': 'swapExactETHForTokens',
      '0x4a25d94a': 'swapTokensForExactETH',
      '0x18cbafe5': 'swapExactTokensForETH',
      '0xfb3bdb41': 'swapETHForExactTokens',
      '0xc04b8d59': 'exactInputSingle', // Uniswap V3 / SushiSwap V3
      '0x5ae401dc': 'multicall' // SushiSwap / Uniswap multicall
    };
    
    console.log('Function:', knownSelectors[functionSelector] || 'Unknown Function');
  }
  
  if (txParams.gasLimit) {
    console.log('Gas Limit:', txParams.gasLimit.toString());
  }
  
  if (txParams.gasPrice) {
    console.log('Gas Price:', txParams.gasPrice.toString());
  }
  
  if (txParams.maxFeePerGas) {
    console.log('Max Fee Per Gas:', txParams.maxFeePerGas.toString());
  }
  
  if (txParams.maxPriorityFeePerGas) {
    console.log('Max Priority Fee Per Gas:', txParams.maxPriorityFeePerGas.toString());
  }
  
  console.groupEnd();
};

/**
 * Analyze and log detailed error information for failed transactions
 * @param {Error} error - Error object
 * @param {object} context - Additional context information
 */
export const analyzeTransactionError = (error, context = {}) => {
  console.group('Transaction Error Analysis');
  console.error('Error:', error.message);
  
  // Extract transaction details if available
  if (error.transaction) {
    console.log('Transaction:', {
      to: error.transaction.to,
      from: error.transaction.from,
      data: error.transaction.data ? 
        `${error.transaction.data.substring(0, 10)}... (${error.transaction.data.length} bytes)` : 
        'No data',
      value: error.transaction.value
    });
  }
  
  // Extract receipt details if available
  if (error.receipt) {
    console.log('Receipt:', {
      status: error.receipt.status,
      gasUsed: error.receipt.gasUsed.toString(),
      blockNumber: error.receipt.blockNumber
    });
  }
  
  // Check for common error patterns
  if (error.message.includes('invalid msg.value')) {
    console.warn('DIAGNOSIS: Transaction included incorrect ETH value amount');
    console.log('SOLUTION: Ensure the exact value from SushiSwap API is used for native token swaps');
  } else if (error.message.includes('insufficient funds')) {
    console.warn('DIAGNOSIS: Wallet has insufficient funds for transaction');
    console.log('SOLUTION: Ensure wallet has enough funds for swap amount plus gas fees');
  } else if (error.message.includes('execution reverted')) {
    console.warn('DIAGNOSIS: Contract execution reverted');
    console.log('SOLUTION: Check slippage settings, transaction deadline, or try a different amount');
  } else if (error.message.includes('transaction discarded')) {
    console.warn('DIAGNOSIS: Transaction rejected by the node');
    console.log('SOLUTION: Remove the "from" field from transaction parameters and try again');
  } else if (error.message.includes('32000')) {
    console.warn('DIAGNOSIS: Network rejected the transaction');
    console.log('SOLUTION: Try again with higher gas settings');
  } else if (error.message.includes('intrinsic gas too low')) {
    console.warn('DIAGNOSIS: Gas limit too low for transaction');
    console.log('SOLUTION: Increase gas limit in transaction parameters');
  } else if (error.message.includes('replacement transaction underpriced')) {
    console.warn('DIAGNOSIS: Attempt to replace transaction with lower gas price');
    console.log('SOLUTION: Use higher gas price when replacing transactions');
  } else if (error.message.includes('nonce too high')) {
    console.warn('DIAGNOSIS: Nonce issue with transaction');
    console.log('SOLUTION: Reset account in wallet or wait for pending transactions to confirm');
  }
  
  // Log context information
  if (Object.keys(context).length > 0) {
    console.log('Additional Context:', context);
  }
  
  console.groupEnd();
};

/**
 * Specialized analyzer for SushiSwap transaction errors
 * @param {Error} error - Error object
 * @param {object} txParams - Transaction parameters
 * @param {object} context - Additional context information
 */
export const analyzeSushiSwapError = (error, txParams, context = {}) => {
  console.group('SushiSwap Transaction Error Analysis');
  console.error('Error:', error.message);
  
  // Log transaction parameters
  if (txParams) {
    console.log('Transaction Parameters:', {
      to: txParams.to,
      value: txParams.value ? txParams.value.toString() : '0',
      dataPrefix: txParams.data ? txParams.data.substring(0, 10) : 'none',
      gasLimit: txParams.gasLimit ? txParams.gasLimit.toString() : 'default'
    });
  }
  
  // Check for common SushiSwap errors
  if (error.message.includes('INSUFFICIENT_OUTPUT_AMOUNT') || 
      error.message.includes('Too little received')) {
    console.warn('DIAGNOSIS: Slippage tolerance exceeded - price moved during transaction');
    console.log('SOLUTION: Try increasing the slippage tolerance or using a smaller amount');
  } else if (error.message.includes('EXCESSIVE_INPUT_AMOUNT')) {
    console.warn('DIAGNOSIS: Price moved favorably during transaction');
    console.log('SOLUTION: This is actually a positive outcome, try the transaction again');
  } else if (error.message.includes('transaction discarded')) {
    console.warn('DIAGNOSIS: Transaction rejected by the node');
    console.log('SOLUTION: Try removing the from field and ensure gas settings are appropriate');
  } else if (error.message.includes('ETH_BALANCE_TOO_LOW') || 
             error.message.includes('insufficient funds')) {
    console.warn('DIAGNOSIS: Wallet has insufficient funds for transaction');
    console.log('SOLUTION: Ensure wallet has enough native token for swap amount plus gas fees');
  } else if (error.message.includes('TRANSFER_FROM_FAILED')) {
    console.warn('DIAGNOSIS: Transfer failed - check token approval');
    console.log('SOLUTION: Try approving the token again with a fresh transaction');
  } else if (error.message.includes('price impact too high')) {
    console.warn('DIAGNOSIS: Price impact exceeds allowed threshold');
    console.log('SOLUTION: Try a smaller amount or use a different token pair');
  }
  
  // Log context information
  if (Object.keys(context).length > 0) {
    console.log('Additional Context:', context);
  }
  
  console.groupEnd();
};